import { faroCoreApiClient } from "@api/faro-core-api-client";
import { createAsyncThunk } from "@reduxjs/toolkit";

/** Payload returned by the group details thunk, including the full projects for the projectsMap */

/**
 * Fetches the list of groups that a company member belongs to.
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyMemberGroups = createAsyncThunk("companyMemberGroups/fetchCompanyMemberGroups", async ({
  memberId
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch company member groups");
  }
  return await faroCoreApiClient.groups.getCompanyMemberGroups({
    companyId: selectedSdbCompanyId,
    userIdentity: memberId
  });
}, {
  condition: (_, api) => {
    // Skip fetching if groups have already been fetched
    return api.getState().companyMemberGroups.status === "uninitialized";
  }
});

/**
 * Fetches the details (including projects) for a single group that a member belongs to.
 * Stores the result as a `CompanyMemberGroupEntity` in the entity adapter.
 *
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyMemberGroupDetails = createAsyncThunk("companyMemberGroups/fetchCompanyMemberGroupDetails", async ({
  groupId,
  userIdentity
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch company member groups");
  }
  const data = await faroCoreApiClient.groups.getCompanyMemberGroupDetails({
    companyId: selectedSdbCompanyId,
    groupId,
    userIdentity
  });
  return {
    projectIds: data.projects.map(project => project.id),
    projects: data.projects,
    explicitRoles: data.explicitRoles
  };
});

/** Payload returned by the thunk that fetches a single group of a member again */

/**
 * Fetches a single group of a member again after their role in it, or in one of its projects,
 * changed. The roles the member inherits from teams only show in what the backend returns, so this
 * is what tells whether the member still holds a role there after a change of their own role.
 *
 * Unlike fetching all the groups, this leaves the fetching status alone, so that the other groups
 * stay shown meanwhile.
 *
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const refreshCompanyMemberGroup = createAsyncThunk("companyMemberGroups/refreshCompanyMemberGroup", async ({
  groupId,
  userIdentity
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to refresh a company member group");
  }
  const groups = await faroCoreApiClient.groups.getCompanyMemberGroups({
    companyId: selectedSdbCompanyId,
    userIdentity
  });
  const summary = groups.find(({
    id
  }) => id === groupId);
  if (!summary) {
    return {
      groupId,
      group: null
    };
  }
  const details = await faroCoreApiClient.groups.getCompanyMemberGroupDetails({
    companyId: selectedSdbCompanyId,
    groupId,
    userIdentity
  });
  return {
    groupId,
    group: {
      ...summary,
      projectIds: details.projects.map(project => project.id),
      projects: details.projects,
      explicitRoles: details.explicitRoles
    }
  };
});